import store from '../store';
import { CookieCategory, waitForCookieAccess } from '@/services/cookieConsent';
import { injectGaScript } from '@/utilities/Tracking';

declare global {
  interface Window {
    gaIntegration?: {
      ga: () => void;
    };
    isPopupV2?: boolean;
    gaInitFn?: () => void;
    gaPageView?: () => void;
    gaUa: () => void;
    gaMainUa?: () => void;
  }
}

interface GaObject {
  name: string;
  storage?: string;
}

interface TrackObject {
  metric1?: number;
  metric2?: number;
  metric3?: string | number;
  page?: string;
  dimension1?: string;
  dimension2?: string;
}

interface TrackingItem {
  id: number;
  type: string;
}

export default function useGaAnalytics() {
  const campaignState = store.getters['campaign/state'];
  const hasGaIntegration = typeof window.gaIntegration !== 'undefined' && window.gaIntegration;
  const tracked: string[] = [];

  const initGa = async () => {
    const isPopup = ['popup', 'popupv2'].find((value) => campaignState?.config?.gameAlias.includes(value));
    if (campaignState && campaignState.config && isPopup) {
      return;
    }
    // For popupv2 we need to include the SDK when ga integration is enabled
    if (window.isPopupV2 && hasGaIntegration) {
      injectGaScript();
      await waitForGa();

      if (typeof window.gaInitFn !== 'undefined') {
        window.gaInitFn();
      }
    }

    if (typeof window.gaPageView !== 'undefined' && typeof window.gaUa !== 'undefined') {
      await waitForCookieAccess(CookieCategory.STATISTICS);
      injectGaScript();
      await waitForGa();
      const gaCreateObject: GaObject = {
        name: 'booztit'
      };

      if (window.gaUa === window.gaMainUa) {
        gaCreateObject.storage = 'none';
      }

      window.ga('create', window.gaUa, 'auto', gaCreateObject);

      if (window.gaUa === window.gaMainUa) {
        window.ga('booztit.set', 'anonymizeIp', true);
      }

      window.ga('booztit.send', 'pageview', window.gaPageView);

      if (typeof window.gaInitFn !== 'undefined') {
        window.gaInitFn();
      }
    } else {
      waitForCookieAccess(CookieCategory.STATISTICS).then(() => {
        injectGaScript();

        if (typeof window.gaInitFn !== 'undefined') {
          window.gaInitFn();
        }
      });
    }
  };

  const waitForGa = () => {
    return new Promise<void>((resolve) => {
      if (typeof window.ga !== 'undefined') {
        resolve();
        return;
      }

      const timer = window.setInterval(() => {
        if (typeof window.ga !== 'undefined') {
          resolve();
          clearInterval(timer);
        }
      }, 1000);
    });
  };

  const handleTracking = async (item: TrackingItem, isFirstFlowPage: boolean) => {
    await initGa();
    await waitForGa();
    await waitForCookieAccess(CookieCategory.STATISTICS);

    if (typeof window.ga !== 'undefined' && item.type && item.type === 'page' && item.id) {
      const id = item.id;
      const trackObject: TrackObject = {
        metric2: campaignState.config?.customerId ? campaignState.config.customerId : undefined,
        metric1: campaignState.config?.campaignId ? campaignState.config.campaignId : undefined
      };

      if (!isNaN(id)) {
        trackObject.metric3 = id;
      } else {
        trackObject.metric3 = item.id;
      }

      if (isFirstFlowPage) {
        trackObject.page = '/customer/' + trackObject.metric2 + '/campaign/' + trackObject.metric1;
      } else {
        trackObject.page =
          '/customer/' + trackObject.metric2 + '/campaign/' + trackObject.metric1 + '/page/' + trackObject.metric3;
      }

      const referenceType = /reference_type:([a-z0-9-_]*)/gi.exec(window.location.href);
      const referenceKey = /reference_key:([a-z0-9-_]*)/gi.exec(window.location.href);

      if (referenceType !== null) {
        trackObject.dimension1 = referenceType[1];
      }

      if (referenceKey !== null) {
        trackObject.dimension2 = referenceKey[1];
      }

      const hash = JSON.stringify(trackObject);

      if (tracked.indexOf(hash) === -1) {
        tracked.push(hash);
        window.ga('booztit.send', 'pageview', trackObject);
      }
    }
  };

  return {
    handleTracking
  };
}
